/**
 * Public /signup page — the PLG entry point.
 *
 * Flow:
 *  1. User fills out org name + admin creds.
 *  2. On blur of the email field we probe /signup/domain-lookup. If we
 *     find a verified domain match, we swap the form for a "join your
 *     company's workspace" CTA that links to /login.
 *  3. Submit calls POST /signup/org, stashes the access token, and
 *     redirects to /onboarding so the first-admin wizard runs immediately.
 *
 * Deliberately NOT wired through AuthContext's login() — that helper
 * assumes a password round-trip. Here the backend mints the token inline
 * as part of org creation, so we write it to localStorage directly and
 * let ProtectedRoute call /auth/me on the next tick.
 */
import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button, Input, Card } from "@/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { setAccessToken } from "@/api/client";
import { signupOrg, domainLookup } from "@/api/signup";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";

interface FoundOrg {
  id: string;
  name: string;
  slug: string;
}

export function SignupPage() {
  const { isAuthenticated } = useAuth();

  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foundOrg, setFoundOrg] = useState<FoundOrg | null>(null);

  if (isAuthenticated) return <Navigate to="/" replace />;

  async function onEmailBlur() {
    setFoundOrg(null);
    if (!email.includes("@")) return;
    try {
      const res = await domainLookup(email);
      if (res.org_id && res.org_name && res.org_slug) {
        setFoundOrg({
          id: res.org_id,
          name: res.org_name,
          slug: res.org_slug,
        });
      }
    } catch {
      // Lookup is advisory — never block the form.
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await signupOrg({
        org_name: orgName.trim(),
        admin_email: email.trim(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        company_size: companySize || undefined,
      });
      setAccessToken(res.access_token);
      // ProtectedRoute will call /auth/me, populate AuthContext, and let the
      // wizard page render. Full reload guarantees a clean auth state.
      window.location.assign("/onboarding");
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Signup failed — please try again.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-lg"
      >
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Lenavio" className="h-10 w-auto" />
          </div>
          <p className="text-text-muted mt-4">
            Start your 3-day trial. No credit card required.
          </p>
        </div>

        {foundOrg ? (
          <Card className="p-6 space-y-4">
            <h2 className="text-lg font-semibold">
              {foundOrg.name} is already on Lenavio
            </h2>
            <p className="text-sm text-text-muted">
              Your company has a verified workspace. Sign in instead of
              creating a duplicate.
            </p>
            <div className="flex gap-2">
              <Link to="/login" className="flex-1">
                <Button className="w-full">Sign in to {foundOrg.name}</Button>
              </Link>
              <Button
                variant="ghost"
                onClick={() => setFoundOrg(null)}
              >
                Not me
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-6">
            <form onSubmit={onSubmit} className="space-y-4">
              <Input
                label="Company name"
                required
                autoComplete="organization"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="First name"
                  required
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <Input
                  label="Last name"
                  required
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <Input
                type="email"
                label="Work email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={onEmailBlur}
              />
              <Input
                type="password"
                label="Password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                hint="At least 8 characters"
              />
              <div className="space-y-1">
                <label
                  htmlFor="company-size"
                  className="block text-sm font-medium text-text-primary"
                >
                  Company size (optional)
                </label>
                <select
                  id="company-size"
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  className="block w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="">Select…</option>
                  <option value="1-10">1–10</option>
                  <option value="10-50">10–50</option>
                  <option value="50-200">50–200</option>
                  <option value="200+">200+</option>
                </select>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
                >
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating workspace…
                  </>
                ) : (
                  "Create workspace"
                )}
              </Button>

              <p className="text-center text-sm text-text-muted">
                Already have an account?{" "}
                <Link to="/login" className="text-accent hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
